import { useEffect, useState } from 'react';
import { Film, Search, X } from 'lucide-react';
import { authFetch } from '@/api/request';
import { cn } from '@/utils/cn';

interface AnimationPickerItem {
  name: string;
  nameEn: string;
  category: 'entrance' | 'exit' | 'loop';
  categoryLabel: string;
  duration: number;
  preview: string;
}

interface AnimationGroup {
  category: 'entrance' | 'exit' | 'loop';
  label: string;
  count: number;
}

interface AnimationPickerProps {
  open: boolean;
  onClose: () => void;
  /** 选中动画后回调；调用方负责把快捷指令拼进输入框 */
  onSelect?: (name: string, item: AnimationPickerItem) => void;
}

/**
 * 动画选择弹窗（聊天框上方"动画"快捷按钮入口）。
 * 数据源：GET /api/playable/animations（animation-library.json，90 条转场动画）。
 * 顶部三个分类 Tab：进场动画 / 退场动画 / 循环动画；
 * 点击动画卡片后关闭弹窗，由调用方在输入框追加"帮我增加{name}动画"。
 */
export function AnimationPicker({ open, onClose, onSelect }: AnimationPickerProps) {
  const [items, setItems] = useState<AnimationPickerItem[]>([]);
  const [groups, setGroups] = useState<AnimationGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<'entrance' | 'exit' | 'loop'>('entrance');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const q = new URLSearchParams();
    if (!search) q.set('category', activeCategory);
    if (search) q.set('q', search);
    authFetch(`/api/playable/animations?${q.toString()}`)
      .then(r => r.json())
      .then(d => {
        setItems(d.animations ?? []);
        setGroups(d.groups ?? []);
      })
      .catch(() => { setItems([]); setGroups([]); })
      .finally(() => setLoading(false));
  }, [open, search, activeCategory]);

  // 打开时重置状态
  useEffect(() => {
    if (open) { setSearch(''); setActiveCategory('entrance'); }
  }, [open]);

  if (!open) return null;

  const visibleGroups: AnimationGroup[] = groups.length
    ? groups
    : [
        { category: 'entrance', label: '进场动画', count: 0 },
        { category: 'exit', label: '退场动画', count: 0 },
        { category: 'loop', label: '循环动画', count: 0 },
      ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="glass-card flex max-h-[80vh] w-full max-w-3xl flex-col overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/30 px-4 py-3">
          <div className="flex items-center gap-2">
            <Film size={16} className="text-neon-cyan" />
            <span className="text-sm font-semibold text-foreground">动画库</span>
            <span className="text-xs text-muted-foreground">选择后自动拼接指令到输入框</span>
          </div>
          <button onClick={onClose} className="icon-btn rounded p-1"><X size={16} /></button>
        </div>

        {/* 分类 Tab：进场 / 退场 / 循环 */}
        <div className="flex items-center gap-2 border-b border-border/30 px-4 pt-2">
          {visibleGroups.map(g => (
            <button
              key={g.category}
              onClick={() => { setActiveCategory(g.category); setSearch(''); }}
              className={cn(
                'rounded-t px-3 py-1.5 text-sm transition-colors',
                !search && activeCategory === g.category
                  ? 'border-b-2 border-neon-cyan font-medium text-neon-cyan'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {g.label}
              {g.count > 0 && <span className="ml-1 text-[10px] text-muted-foreground">({g.count})</span>}
            </button>
          ))}
        </div>

        <div className="border-b border-border/30 px-4 py-2">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              className="input-neon w-full py-2 pl-8 pr-3 text-sm"
              placeholder="搜索动画名称，如：弹动、淡入、BounceIn..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-xs text-muted-foreground">加载中...</div>
          ) : items.length === 0 ? (
            <div className="flex items-center justify-center py-20 text-xs text-muted-foreground">暂无匹配的动画</div>
          ) : (
            <div className="grid grid-cols-4 gap-3">
              {items.map((a, idx) => (
                <button
                  // DO-NOT-REVERT: key 必须含 idx——animation-library.json 曾出现同 category+nameEn 重复（SlideY/SlideX），见 ISSUE-LEDGER BUG-20260821-PW-001
                  key={`${a.category}-${a.nameEn}-${idx}`}
                  onClick={() => { onSelect?.(a.name, a); onClose(); }}
                  className="glass-card card-lift cursor-pointer overflow-hidden text-left transition-all hover:border-primary/30"
                >
                  <div className="flex aspect-square items-center justify-center bg-muted/30">
                    {a.preview
                      ? <img src={a.preview} alt={a.name} loading="lazy" className="h-full w-full object-contain p-2" />
                      : <Film size={28} className="text-muted-foreground/30" />}
                  </div>
                  <div className="border-t border-border/20 p-2">
                    <p className="text-xs font-medium text-foreground">{a.name}</p>
                    <p className="mt-0.5 truncate text-[9px] text-muted-foreground">{a.nameEn} · {a.duration}s</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
